import React from "react";
import { FiMessageSquare } from "react-icons/fi";
import { useSelector } from "react-redux";

const ACTIVE_STATUSES = new Set(["accepted", "confirmed", "active", "ongoing"]);

const reqId = (r) => r._id || r.id;

const getOtherPartyId = (req, role) => {
  if (role === "therapist") {
    const u = req.userId;
    return typeof u === "object" ? u._id || u.id : u;
  } else {
    const t = req.therapistId;
    return typeof t === "object" ? t._id || t.id : t;
  }
};

const getOtherPartyInfo = (req, role) => {
  if (role === "therapist") {
    const u = req.userId;
    if (!u || typeof u !== "object") return { name: "Client", picture: null };
    const name = `${u.name || ""} ${u.surname || ""}`.trim() || u.email || "Client";
    const picture = u.profileImage || u.picture || null;
    return { name, picture };
  } else {
    const t = req.therapistId;
    if (!t || typeof t !== "object") return { name: "Therapist", picture: null };
    const name = `${t.name || ""} ${t.surname || ""}`.trim() || t.email || "Therapist";
    const picture = t.picture || t.profileImage || null;
    return { name, picture };
  }
};

const Avatar = ({ name, picture, size = "md" }) => {
  const dim = size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
  if (picture) {
    return (
      <img src={picture} alt={name} className={`${dim} rounded-full object-cover shrink-0`} />
    );
  }
  return (
    <div className={`${dim} rounded-full bg-[#E5D3DD] flex items-center justify-center font-semibold text-[#7a4f6a] shrink-0`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
};

const ConversationList = ({ requests, selectedId, onSelect, role }) => {
  const unreadByRequest = useSelector((state) => state.chat.unreadByRequest);

  // Sadece aktif requestleri al
  const activeRequests = requests.filter((r) =>
    ACTIVE_STATUSES.has(r.status?.toLowerCase())
  );

  // Aynı terapist/danışan için tek sohbet: kişi başına en yeni request'i al
  const groupedMap = new Map();
  activeRequests.forEach((req) => {
    const partyId = getOtherPartyId(req, role);
    if (!partyId) return;
    const existing = groupedMap.get(partyId);
    if (!existing) {
      groupedMap.set(partyId, req);
    } else {
      // En yeni request'i tut (updatedAt veya createdAt'e göre)
      const existingDate = new Date(existing.updatedAt || existing.createdAt || 0);
      const newDate = new Date(req.updatedAt || req.createdAt || 0);
      if (newDate > existingDate) groupedMap.set(partyId, req);
    }
  });

  // En son aktif olan üste gelsin
  const conversations = Array.from(groupedMap.values()).sort((a, b) => {
    const da = new Date(a.updatedAt || a.createdAt || 0);
    const db = new Date(b.updatedAt || b.createdAt || 0);
    return db - da;
  });

  // Her kişi için toplam okunmamış mesaj sayısını hesapla
  const getPartyUnread = (partyId) => {
    let total = 0;
    activeRequests.forEach((req) => {
      if (getOtherPartyId(req, role) === partyId) {
        total += unreadByRequest[reqId(req)] || 0;
      }
    });
    return total;
  };

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 text-gray-400 gap-3">
        <FiMessageSquare className="w-10 h-10" />
        <p className="text-sm">No active sessions</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {conversations.map((req) => {
        const id = reqId(req);
        const partyId = getOtherPartyId(req, role);
        const { name, picture } = getOtherPartyInfo(req, role);
        const isSelected = id === selectedId;
        const unread = getPartyUnread(partyId);

        return (
          <button
            key={partyId || id}
            onClick={() => onSelect(req)}
            className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
              isSelected ? "bg-[#E5D3DD]/40" : "hover:bg-gray-50"
            }`}
          >
            <div className="relative shrink-0">
              <Avatar name={name} picture={picture} />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none border border-white">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-sm truncate ${
                isSelected ? "text-[#7a4f6a] font-semibold"
                  : unread > 0 ? "text-gray-900 font-bold"
                  : "text-gray-700 font-medium"
              }`}>
                {name}
              </p>
              <p className={`text-xs ${unread > 0 ? "text-[#7a4f6a] font-medium" : "text-gray-400"}`}>
                {unread > 0
                  ? `${unread} new message${unread > 1 ? "s" : ""}`
                  : "Active"}
              </p>
            </div>
            {unread > 0 && (
              <span className="ml-auto shrink-0 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ConversationList;
